<template>
  <app-client-simple-layout title="ログイン">
    <div class="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md bg-white shadow-lg rounded-lg p-8">
        <h1 class="text-xl font-bold text-center mb-6">ログイン</h1>
        <div class="mb-5 text-sm text-center text-gray-600">全国統一模試と同じアカウントをご利用いただけます。</div>

        <jet-validation-errors class="mb-5" />

        <form @submit.prevent="signIn">
          <label for="email">
            メールアドレス
          </label>
          <input
            v-model="email"
            class="block mt-2 mb-2 w-full border-1 border-gray-500 rounded"
            type="email"
            required
            placeholder="メールアドレス"
            autocomplete="email" />
          <div
            v-if="emailFormErrorText"
            class="text-red-500 mb-5">
            ※{{ emailFormErrorText }}
          </div>

          <label for="password">
            パスワード
          </label>
          <input
            v-model="password"
            class="block mt-2 mb-2 w-full border-1 border-gray-500 rounded"
            type="password"
            required
            placeholder="パスワード(6文字以上)"
            autocomplete="current-password" />
          <div
            v-if="passwordFormErrorText"
            class="text-red-500 mb-5">
            ※{{ passwordFormErrorText }}
          </div>

          <a
            :href="route('client.password.reset')"
            class="mb-5 flex justify-center underline text-blue-500 hover:underline">
            パスワードを忘れた方はこちら
          </a>

          <div
            v-if="loginErrorText"
            class="whitespace-pre-line text-red-500 my-5">
            {{ loginErrorText }}
          </div>

          <button
            :disabled="isProcessing"
            class="flex justify-self-end text-white text-center text-base bg-CLIENT_PRIMARY_PINK rounded-xl
                mx-auto mt-2 px-10 py-2 hover:bg-CLIENT_PRIMARY_PINK_HOVER duration-500 disabled:bg-gray-300
                disabled:cursor-not-allowed">
            ログイン
          </button>
        </form>

        <div class="mt-6 text-center space-y-3">
          <a
            :href="route('clients.signup.index')"
            class="block text-blue-500 underline hover:underline">
            無料会員登録はこちら
          </a>
          <a
            :href="publicPortalUrl"
            class="block text-gray-500 underline hover:underline text-sm">
            トップページに戻る
          </a>
        </div>
      </div>
    </div>
  </app-client-simple-layout>
</template>

<script setup>
import { ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { usePage } from '@inertiajs/inertia-vue3'
import firebase from 'firebase/compat/app'
import {
  getAuth,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import 'firebase/compat/auth'
import AppClientSimpleLayout from '@/Layouts/AppClientSimpleLayout'
import JetValidationErrors from '@/Jetstream/ValidationErrors'
import firebaseApp from '@/Constants/configFirebase'
import { EmailRegex } from '@/Constants/regularExpression'
import SIGNIN_ERROR_MESSAGES from '@/Constants/signinErrors'

const props = defineProps({
  isExam: {
    type: Boolean,
    default: false,
  },
})

const auth = getAuth(firebaseApp)
const publicPortalUrl = usePage().props.value.portal.publicUrl

const email = ref('')
const password = ref('')

const emailFormErrorText = ref('')
const passwordFormErrorText = ref('')
const loginErrorText = ref('')

const isProcessing = ref(false)

const signIn = async () => {
  // 未入力＆フォーマットチェック
  if (!email.value.length) {
    emailFormErrorText.value = SIGNIN_ERROR_MESSAGES.emptyEmail
  } else if (!email.value.match(EmailRegex)) {
    emailFormErrorText.value = SIGNIN_ERROR_MESSAGES.invalidEmail
  } else {
    emailFormErrorText.value = ''
  }
  if (!password.value.length) {
    passwordFormErrorText.value = SIGNIN_ERROR_MESSAGES.emptyPassword
  } else {
    passwordFormErrorText.value = ''
  }

  // ERRORがあれば、処理を中断する
  if (emailFormErrorText.value || passwordFormErrorText.value) {
    return false
  }

  // 多重ポストを避ける
  if (isProcessing.value) return false
  isProcessing.value = true

  // firebase にログイン
  try {
    loginErrorText.value = ''
    await firebase.auth(firebaseApp).signInWithEmailAndPassword(email.value, password.value)

    // 正常系
    // uidを受け取れるトークンを取得
    const userToken = await firebase.auth(firebaseApp).currentUser.getIdToken(true)
    const userEmail = firebase.auth(firebaseApp).currentUser.email

    // Laravel側の認証
    Inertia.visit(route('clients.login.post'), {
      method: 'post',
      data: {
        email: userEmail,
        password: password.value,
        idToken: userToken,
        isExam: props.isExam,
      },
    })
  } catch (err) {
    isProcessing.value = false

    // 異常系
    if (err.code === 'auth/user-not-found') {
      loginErrorText.value = 'ユーザーが存在しません。\n入力情報を再度ご確認ください。'
    }
    if (err.code === 'auth/wrong-password') {
      loginErrorText.value = 'パスワードに誤りがあります。\n入力情報を再度ご確認ください。'
    }
    return false
  }
  return false
}
</script>
